import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import ScoutSetupForm from '../components/ScoutSetupForm';
import { storage } from '../../firebaseConfig';
import {
  extractScoutFromCv,
  startOnboardingConversation,
} from '../onboardingAgent';
import { useLanguage } from '../contexts/LanguageContext';
import locationOptions from '../locationOptions.json';

const ONBOARDING_STEPS = {
  NAME: 'name',
  CV: 'cv',
  AGENT: 'agent',
};

const SUPPORTED_AGENT_QUESTION_TYPES = new Set([
  'text',
  'single-select',
  'multi-select',
]);
const DEPRECATED_SCOUT_ESSENTIALS_QUESTION_IDS = new Set(['scoutEssentials']);
const DEPRECATED_SCOUT_ESSENTIALS_QUESTION_TYPES = new Set([
  'scout-essentials',
]);
const DEFAULT_PHONE_COUNTRY_CODE = '+961';
export const UNITED_STATES_NAME = 'United States';
export const US_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
];

const PHONE_COUNTRY_CODES = [
  ['Lebanon', '+961'],
  ['Afghanistan', '+93'],
  ['Albania', '+355'],
  ['Algeria', '+213'],
  ['Argentina', '+54'],
  ['Armenia', '+374'],
  ['Australia', '+61'],
  ['Austria', '+43'],
  ['Azerbaijan', '+994'],
  ['Bahrain', '+973'],
  ['Bangladesh', '+880'],
  ['Belarus', '+375'],
  ['Belgium', '+32'],
  ['Bolivia', '+591'],
  ['Bosnia and Herzegovina', '+387'],
  ['Brazil', '+55'],
  ['Bulgaria', '+359'],
  ['Cambodia', '+855'],
  ['Cameroon', '+237'],
  ['Canada', '+1'],
  ['Chile', '+56'],
  ['China', '+86'],
  ['Colombia', '+57'],
  ['Costa Rica', '+506'],
  ['Croatia', '+385'],
  ['Cyprus', '+357'],
  ['Czechia', '+420'],
  ['Denmark', '+45'],
  ['Dominican Republic', '+1'],
  ['Ecuador', '+593'],
  ['Egypt', '+20'],
  ['Estonia', '+372'],
  ['Ethiopia', '+251'],
  ['Finland', '+358'],
  ['France', '+33'],
  ['Georgia', '+995'],
  ['Germany', '+49'],
  ['Ghana', '+233'],
  ['Greece', '+30'],
  ['Hong Kong', '+852'],
  ['Hungary', '+36'],
  ['Iceland', '+354'],
  ['India', '+91'],
  ['Indonesia', '+62'],
  ['Iran', '+98'],
  ['Iraq', '+964'],
  ['Ireland', '+353'],
  ['Israel', '+972'],
  ['Italy', '+39'],
  ['Japan', '+81'],
  ['Jordan', '+962'],
  ['Kazakhstan', '+7'],
  ['Kenya', '+254'],
  ['Kuwait', '+965'],
  ['Latvia', '+371'],
  ['Libya', '+218'],
  ['Lithuania', '+370'],
  ['Luxembourg', '+352'],
  ['Malaysia', '+60'],
  ['Malta', '+356'],
  ['Mexico', '+52'],
  ['Moldova', '+373'],
  ['Morocco', '+212'],
  ['Nepal', '+977'],
  ['Netherlands', '+31'],
  ['New Zealand', '+64'],
  ['Nigeria', '+234'],
  ['North Macedonia', '+389'],
  ['Norway', '+47'],
  ['Oman', '+968'],
  ['Pakistan', '+92'],
  ['Palestine', '+970'],
  ['Panama', '+507'],
  ['Peru', '+51'],
  ['Philippines', '+63'],
  ['Poland', '+48'],
  ['Portugal', '+351'],
  ['Qatar', '+974'],
  ['Romania', '+40'],
  ['Russia', '+7'],
  ['Saudi Arabia', '+966'],
  ['Serbia', '+381'],
  ['Singapore', '+65'],
  ['Slovakia', '+421'],
  ['Slovenia', '+386'],
  ['South Africa', '+27'],
  ['South Korea', '+82'],
  ['Spain', '+34'],
  ['Sri Lanka', '+94'],
  ['Sudan', '+249'],
  ['Sweden', '+46'],
  ['Switzerland', '+41'],
  ['Syria', '+963'],
  ['Taiwan', '+886'],
  ['Thailand', '+66'],
  ['Tunisia', '+216'],
  ['Turkey', '+90'],
  ['Ukraine', '+380'],
  ['United Arab Emirates', '+971'],
  ['United Kingdom', '+44'],
  ['United States', '+1'],
  ['Uruguay', '+598'],
  ['Uzbekistan', '+998'],
  ['Venezuela', '+58'],
  ['Vietnam', '+84'],
  ['Yemen', '+967'],
  ['Andorra', '+376'],
  ['Angola', '+244'],
  ['Antigua and Barbuda', '+1268'],
  ['Bahamas', '+1242'],
  ['Barbados', '+1246'],
  ['Belize', '+501'],
  ['Benin', '+229'],
  ['Bhutan', '+975'],
  ['Botswana', '+267'],
  ['Brunei', '+673'],
  ['Burkina Faso', '+226'],
  ['Burundi', '+257'],
  ['Cabo Verde', '+238'],
  ['Central African Republic', '+236'],
  ['Chad', '+235'],
  ['Comoros', '+269'],
  ['Congo', '+242'],
  ['Democratic Republic of the Congo', '+243'],
  ['Cuba', '+53'],
  ['Djibouti', '+253'],
  ['Dominica', '+1767'],
  ['El Salvador', '+503'],
  ['Equatorial Guinea', '+240'],
  ['Eritrea', '+291'],
  ['Eswatini', '+268'],
  ['Fiji', '+679'],
  ['Gabon', '+241'],
  ['Gambia', '+220'],
  ['Grenada', '+1473'],
  ['Guatemala', '+502'],
  ['Guinea', '+224'],
  ['Guinea-Bissau', '+245'],
  ['Guyana', '+592'],
  ['Haiti', '+509'],
  ['Honduras', '+504'],
  ['Ivory Coast', '+225'],
  ['Jamaica', '+1876'],
  ['Kiribati', '+686'],
  ['Kyrgyzstan', '+996'],
  ['Laos', '+856'],
  ['Lesotho', '+266'],
  ['Liberia', '+231'],
  ['Liechtenstein', '+423'],
  ['Madagascar', '+261'],
  ['Malawi', '+265'],
  ['Maldives', '+960'],
  ['Mali', '+223'],
  ['Marshall Islands', '+692'],
  ['Mauritania', '+222'],
  ['Mauritius', '+230'],
  ['Micronesia', '+691'],
  ['Monaco', '+377'],
  ['Mongolia', '+976'],
  ['Montenegro', '+382'],
  ['Mozambique', '+258'],
  ['Myanmar', '+95'],
  ['Namibia', '+264'],
  ['Nauru', '+674'],
  ['Nicaragua', '+505'],
  ['Niger', '+227'],
  ['North Korea', '+850'],
  ['Palau', '+680'],
  ['Papua New Guinea', '+675'],
  ['Paraguay', '+595'],
  ['Rwanda', '+250'],
  ['Saint Kitts and Nevis', '+1869'],
  ['Saint Lucia', '+1758'],
  ['Saint Vincent and the Grenadines', '+1784'],
  ['Samoa', '+685'],
  ['San Marino', '+378'],
  ['Sao Tome and Principe', '+239'],
  ['Senegal', '+221'],
  ['Seychelles', '+248'],
  ['Sierra Leone', '+232'],
  ['Solomon Islands', '+677'],
  ['Somalia', '+252'],
  ['South Sudan', '+211'],
  ['Suriname', '+597'],
  ['Tajikistan', '+992'],
  ['Tanzania', '+255'],
  ['Timor-Leste', '+670'],
  ['Togo', '+228'],
  ['Tonga', '+676'],
  ['Trinidad and Tobago', '+1868'],
  ['Turkmenistan', '+993'],
  ['Tuvalu', '+688'],
  ['Uganda', '+256'],
  ['Vanuatu', '+678'],
  ['Vatican City', '+39'],
  ['Zambia', '+260'],
  ['Zimbabwe', '+263'],
].sort(([leftCountry], [rightCountry]) => {
  if (leftCountry === 'Lebanon') return -1;
  if (rightCountry === 'Lebanon') return 1;
  return leftCountry.localeCompare(rightCountry);
});

const KNOWN_PHONE_COUNTRY_CODES = [
  ...new Set(PHONE_COUNTRY_CODES.map(([, code]) => code)),
].sort((left, right) => right.length - left.length);

export function splitStoredPhoneNumber(value) {
  const storedValue = String(value || '').trim();
  const matchedCode = KNOWN_PHONE_COUNTRY_CODES.find((code) =>
    storedValue.startsWith(code),
  );

  if (!matchedCode) {
    return {
      countryCode: DEFAULT_PHONE_COUNTRY_CODE,
      localNumber: storedValue,
    };
  }

  return {
    countryCode: matchedCode,
    localNumber: storedValue.slice(matchedCode.length).trim(),
  };
}

export function formatPhoneNumber(countryCode, localNumber) {
  const normalizedLocalNumber = String(localNumber || '')
    .trim()
    .replace(/^\+\d{1,4}[\s-]*/, '')
    .replace(/\s+/g, ' ');

  return normalizedLocalNumber ? `${countryCode} ${normalizedLocalNumber}` : '';
}

export function shouldShowCvPrefillNote({
  cvUploaded,
  suggestedAnswer,
  typedAnswer,
}) {
  return Boolean(
    cvUploaded &&
    typeof suggestedAnswer === 'string' &&
    suggestedAnswer.trim() &&
    String(typedAnswer || '').trim(),
  );
}

export function parseTargetCityAnswer(value) {
  const text = String(value || '').trim();
  if (text.toLowerCase() === 'anywhere')
    return { allCities: true, locations: [] };

  const locations = text
    .split(/[;\n|]+/)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const parts = entry
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
      if (parts.length < 2) return null;
      const country = parts[parts.length - 1];
      const state =
        country === UNITED_STATES_NAME && parts.length >= 3
          ? parts[parts.length - 2]
          : '';
      const city = parts
        .slice(0, state ? -2 : -1)
        .join(', ')
        .trim();
      return city && country
        ? { city, ...(state ? { state } : {}), country }
        : null;
    })
    .filter(Boolean);

  return { allCities: false, locations };
}

export function formatTargetCityAnswer(allCities, locations) {
  if (allCities) return 'Anywhere';
  return (locations || [])
    .filter(
      ({ city, country }) =>
        String(city || '').trim() && String(country || '').trim(),
    )
    .map(({ city, state, country }) =>
      [city, state, country]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join(', '),
    )
    .join('; ');
}

function normalizedNameCandidate(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s._-]+/g, '');
}

function isEmailDerivedName(value, email) {
  const candidate = String(value || '').trim();
  if (!candidate || candidate.includes('@'))
    return Boolean(candidate.includes('@'));

  const emailLocalPart = String(email || '')
    .trim()
    .split('@')[0];
  return (
    Boolean(emailLocalPart) &&
    normalizedNameCandidate(candidate) ===
      normalizedNameCandidate(emailLocalPart)
  );
}

export function getNameParts(profile, user) {
  const email = profile?.email || user?.email || '';
  const explicitFirstName = String(profile?.firstName || '').trim();
  const explicitLastName = String(profile?.lastName || '').trim();

  if (
    (explicitFirstName || explicitLastName) &&
    !explicitFirstName.includes('@') &&
    !explicitLastName.includes('@')
  ) {
    return { firstName: explicitFirstName, lastName: explicitLastName };
  }

  const displayName =
    [profile?.username, user?.displayName]
      .map((value) => String(value || '').trim())
      .find(
        (value) =>
          value &&
          normalizedNameCandidate(value) !== 'jobseeker' &&
          !isEmailDerivedName(value, email),
      ) || '';
  const [firstName = '', ...rest] = displayName
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return {
    firstName,
    lastName: rest.join(' '),
  };
}

export function isDeprecatedScoutEssentialsQuestion(question) {
  if (!question) return false;
  return (
    DEPRECATED_SCOUT_ESSENTIALS_QUESTION_IDS.has(question.id) ||
    DEPRECATED_SCOUT_ESSENTIALS_QUESTION_TYPES.has(question.type)
  );
}

export function isSupportedAgentQuestion(question) {
  return Boolean(question) && SUPPORTED_AGENT_QUESTION_TYPES.has(question.type);
}

export function isFinalAgentQuestion(conversation) {
  return (
    Boolean(conversation?.question) &&
    (conversation.questionQueue || []).length === 0
  );
}

export function hasMeaningfulScoutExtraction(extractedProfile) {
  const cv = extractedProfile?.cv || {};
  const goals = extractedProfile?.goals || {};
  const locations = extractedProfile?.locationPreferences || {};
  return Boolean(
    cv.currentTitle ||
    cv.headline ||
    (cv.pastTitles || []).length ||
    (cv.skills || []).length ||
    (goals.targetTitles || []).length ||
    (goals.targetRoleFamilies || []).length ||
    (locations.targetCountries || []).length ||
    locations.residenceCountry,
  );
}

export function preferMeaningfulScoutExtraction(
  currentProfile,
  incomingProfile,
) {
  if (
    hasMeaningfulScoutExtraction(currentProfile) &&
    !hasMeaningfulScoutExtraction(incomingProfile)
  ) {
    return currentProfile;
  }
  return incomingProfile && Object.keys(incomingProfile).length
    ? incomingProfile
    : currentProfile;
}

export function questionWithExtractionSuggestion(question, extractedProfile) {
  if (!question || !extractedProfile) return question;

  if (question.suggestedAnswer) return question;

  const goals = extractedProfile.goals || {};
  const cv = extractedProfile.cv || {};
  const locations = extractedProfile.locationPreferences || {};

  if (question.id === 'targetDirection') {
    const titles = [
      ...(goals.targetTitles || []),
      cv.currentTitle,
      ...(cv.pastTitles || []).slice(0, 3),
    ].filter(Boolean);
    const suggestedAnswer = [...new Set(titles)].slice(0, 4).join('; ');
    return suggestedAnswer ? { ...question, suggestedAnswer } : question;
  }

  if (question.id === 'workSetup') {
    const values = [...(locations.remotePreference || [])];
    if (locations.relocationOpen) values.push('open_to_relocation');
    const allowed = new Set(
      (question.options || []).map((option) => option.value),
    );
    const suggestedAnswer = [...new Set(values)].filter((value) =>
      allowed.has(value),
    );
    return suggestedAnswer.length ? { ...question, suggestedAnswer } : question;
  }

  if (question.id === 'seniorityPreference') {
    const normalized = String((goals.seniority || [])[0] || '')
      .trim()
      .toLowerCase();
    const allowed = new Set(
      (question.options || []).map((option) => option.value),
    );
    return allowed.has(normalized)
      ? { ...question, suggestedAnswer: normalized }
      : question;
  }

  if (question.id === 'jobTypes') {
    const allowed = new Set(
      (question.options || []).map((option) => option.value),
    );
    const suggestedAnswer = [...new Set(goals.jobTypes || [])].filter((value) =>
      allowed.has(value),
    );
    return suggestedAnswer.length ? { ...question, suggestedAnswer } : question;
  }

  if (question.id === 'residenceCountry') {
    return locations.residenceCountry
      ? { ...question, suggestedAnswer: locations.residenceCountry }
      : question;
  }

  if (question.id === 'targetCountries') {
    const countries = [
      ...(locations.targetCountries || []),
      ...(locations.countries || []),
    ].filter(Boolean);
    const suggestedAnswer = [...new Set(countries)].slice(0, 4).join('; ');
    return suggestedAnswer ? { ...question, suggestedAnswer } : question;
  }

  if (question.id === 'professionalLanguages') {
    const suggestedAnswer = [...new Set(cv.languages || [])]
      .filter(Boolean)
      .join('; ');
    return suggestedAnswer ? { ...question, suggestedAnswer } : question;
  }

  return question;
}

function OnboardingScreen({
  profile,
  user,
  onProfileSaved,
  onOnboardingSubmitted,
  onOnboardingExited,
}) {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const countryLabels = useMemo(() => {
    const names = new Intl.DisplayNames([language], { type: 'region' });
    return {
      ...Object.fromEntries(locationOptions.countries.map(({ name, code }) =>
        [name, language === 'ar' ? names.of(code) || name : name],
      )),
      Congo: language === 'ar' ? names.of('CG') : 'Congo',
      'Ivory Coast': language === 'ar' ? names.of('CI') : 'Ivory Coast',
    };
  }, [language]);
  const to = useCallback((key) => t(`onboardingPage.${key}`), [t]);
  const fill = (template, values) =>
    Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`{${key}}`, String(value)),
      template,
    );
  const initialName = useMemo(
    () => getNameParts(profile, user),
    [profile, user],
  );
  const initialPhone = useMemo(
    () => splitStoredPhoneNumber(profile?.phoneNumber),
    [profile?.phoneNumber],
  );
  const [step, setStep] = useState(
    initialName.firstName && initialName.lastName
      ? ONBOARDING_STEPS.CV
      : ONBOARDING_STEPS.NAME,
  );
  const [firstName, setFirstName] = useState(initialName.firstName);
  const [lastName, setLastName] = useState(initialName.lastName);
  const [phoneCountryCode, setPhoneCountryCode] = useState(
    initialPhone.countryCode,
  );
  const [phoneNumber, setPhoneNumber] = useState(initialPhone.localNumber);
  const [cvFile, setCvFile] = useState(null);
  const [conversation, setConversation] = useState(null);
  const conversationRef = useRef(null);
  const extractionRef = useRef(null);
  const generationRef = useRef(0);
  const profileSaveRef = useRef(null);
  const retryProfileSaveRef = useRef(null);
  const [cvPending, setCvPending] = useState(false);
  const [cvFailed, setCvFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);
  const displayFirstName =
    firstName.trim() || (language === 'ar' ? 'بك' : 'there');
  useEffect(
    () => () => {
      generationRef.current += 1;
    },
    [],
  );

  const saveNameStep = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setErrorMessage(to('errors.nameRequired'));
      return;
    }
    setSaving(true);
    setErrorMessage('');
    try {
      await onProfileSaved({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        username: `${firstName.trim()} ${lastName.trim()}`,
        onboardingStatus: 'cv_pending',
        onboardingUpdatedAt: Date.now(),
      });
      setStep(ONBOARDING_STEPS.CV);
    } catch {
      setErrorMessage(to('errors.saveName'));
    } finally {
      setSaving(false);
    }
  };
  const startAgentStep = async ({ skipCv = false } = {}) => {
    if (saving) return;
    setSaving(true);
    setErrorMessage('');
    setCvFailed(false);
    const selectedCv = skipCv ? null : cvFile;
    if (selectedCv && selectedCv.type !== 'application/pdf') {
      setErrorMessage(to('errors.pdf'));
      setSaving(false);
      return;
    }
    const generation = ++generationRef.current;
    const cvStoragePath = selectedCv ? `Images/${user.uid}/cv.pdf` : '';
    try {
      if (selectedCv)
        await uploadBytes(storageRef(storage, cvStoragePath), selectedCv);
      const initial = {
        locale: language,
        answers: {},
        questionQueue: [],
        extractedProfile: {},
        chatBootstrap: {},
        cvStoragePath,
        cvFileName: selectedCv?.name || '',
        messages: [],
      };
      conversationRef.current = initial;
      setConversation(initial);
      setStep(ONBOARDING_STEPS.AGENT);
      setSaving(false);
      const args = {
        user,
        firstName: displayFirstName,
        cvStoragePath,
        cvFileName: selectedCv?.name || '',
      };
      retryProfileSaveRef.current = async () => {
        const update = {
          phoneNumber: formatPhoneNumber(phoneCountryCode, phoneNumber),
          onboardingStatus: 'agent_in_progress',
          onboardingUpdatedAt: Date.now(),
        };
        if (selectedCv) {
          update.cv = {
            Link: await getDownloadURL(storageRef(storage, cvStoragePath)),
          };
          update.cvMetadata = {
            name: selectedCv.name,
            size: selectedCv.size,
            type: selectedCv.type,
            storagePath: cvStoragePath,
            capturedAt: Date.now(),
          };
        } else {
          update.cvSkippedAt = Date.now();
        }
        await onProfileSaved(update);
      };
      profileSaveRef.current = retryProfileSaveRef.current();
      // Attach a handler immediately; submit retries a failed profile save visibly.
      profileSaveRef.current.catch(() => {
        if (generation === generationRef.current)
          setErrorMessage(to('errors.saveName'));
      });
      startOnboardingConversation(args)
        .then((response) => {
          if (generation !== generationRef.current) return;
          conversationRef.current = {
            ...conversationRef.current,
            chatBootstrap: response.chatBootstrap || {},
            messages: response.messages || [],
          };
          setConversation(conversationRef.current);
        })
        .catch(() => {}); // Personalization is optional; the form remains usable.
      setCvPending(Boolean(selectedCv));
      extractionRef.current = selectedCv
        ? extractScoutFromCv(args)
            .then((response) => {
              if (generation !== generationRef.current) return;
              conversationRef.current = {
                ...conversationRef.current,
                extractedProfile: response.extractedProfile || {},
              };
              setConversation(conversationRef.current);
            })
            .catch(() => {
              if (generation === generationRef.current) setCvFailed(true);
            })
            .finally(() => {
              if (generation === generationRef.current) setCvPending(false);
            })
        : Promise.resolve();
    } catch {
      setErrorMessage(to('errors.upload'));
      setSaving(false);
    }
  };
  const complete = async (answers, referral) => {
    setSaving(true);
    setErrorMessage('');
    try {
      try {
        await profileSaveRef.current;
      } catch {
        profileSaveRef.current = retryProfileSaveRef.current();
        await profileSaveRef.current;
      }
      // The durable worker handles extraction if the CV is still being read.
      const latest = conversationRef.current;
      await onOnboardingSubmitted({
        conversation: { ...latest, answers, questionQueue: [] },
        question: { id: 'referralSource', type: 'single-select' },
        answer: referral,
      });
      generationRef.current += 1;
      onOnboardingExited?.();
      navigate('/opportunities', { replace: true });
    } finally {
      setSaving(false);
    }
  };
  const scoutId = profile?.primaryScoutId;
  if (
    profile?.onboardingStatus === 'complete' &&
    scoutId &&
    profile?.jobScouts?.[scoutId]?.scoutId === scoutId
  )
    return <Navigate to="/opportunities" replace />;
  return (
    <main
      className={`js-onboarding ${step === ONBOARDING_STEPS.NAME ? 'is-name-step' : ''} ${step === ONBOARDING_STEPS.AGENT ? 'is-chat-step' : ''}`}
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="js-onboarding__badge">{to('mode')}</div>
      {step === ONBOARDING_STEPS.NAME && (
        <section className="js-onboarding__card is-compact">
          <h1>{to('niceToMeet')}</h1>

          <div className="js-onboarding__stack">
            <input
              className="js-onboarding__input"
              type="text"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              placeholder={to('firstName')}
            />
            <input
              className="js-onboarding__input"
              type="text"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              placeholder={to('lastName')}
            />
            <button
              type="button"
              className="js-onboarding__button"
              onClick={saveNameStep}
              disabled={saving}
            >
              {saving ? to('saving') : to('continue')}
            </button>
          </div>
        </section>
      )}

      {step === ONBOARDING_STEPS.CV && (
        <section className="js-onboarding__card">
          <h1>{fill(to('welcome'), { name: displayFirstName })}</h1>
          <p>{to('cvHelp')}</p>

          <div className="js-onboarding__stack">
            <button
              type="button"
              className="js-onboarding__file"
              onClick={() => fileInputRef.current?.click()}
            >
              {cvFile ? cvFile.name : to('uploadCv')}
            </button>
            <input
              ref={fileInputRef}
              className="js-onboarding__file-input"
              type="file"
              accept="application/pdf"
              onChange={(event) => setCvFile(event.target.files?.[0] || null)}
            />

            <p>{to('phoneHelp')}</p>
            <div className="js-onboarding__phone-field">
              <select
                className="js-onboarding__phone-code"
                value={phoneCountryCode}
                onChange={(event) => setPhoneCountryCode(event.target.value)}
                aria-label={to('phoneCode')}
              >
                {PHONE_COUNTRY_CODES.map(([country, code]) => (
                  <option key={`${country}-${code}`} value={code}>
                    {countryLabels[country] || country} ({code})
                  </option>
                ))}
              </select>
              <input
                className="js-onboarding__input"
                type="tel"
                dir="ltr"
                inputMode="tel"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                placeholder={to('phone')}
              />
            </div>

            <button
              type="button"
              className="js-onboarding__button"
              onClick={() => startAgentStep()}
              disabled={saving}
            >
              {saving ? to('starting') : to('continue')}
            </button>
            <button
              type="button"
              className="js-onboarding__text-button"
              onClick={() => startAgentStep({ skipCv: true })}
            >
              {to('noCv')}
            </button>
          </div>
        </section>
      )}

      {step === ONBOARDING_STEPS.AGENT && conversation && (
        <ScoutSetupForm
          extractedProfile={conversation.extractedProfile}
          bootstrap={conversation.chatBootstrap}
          messages={conversation.messages}
          firstName={firstName.trim()}
          cvPending={cvPending}
          cvFailed={cvFailed}
          busy={saving}
          onSubmit={complete}
          onBack={() => {
            generationRef.current += 1;
            setStep(ONBOARDING_STEPS.CV);
          }}
        />
      )}
      {errorMessage && (
        <div className="js-onboarding__error" role="alert">
          <p>{errorMessage}</p>
        </div>
      )}
    </main>
  );
}
export default OnboardingScreen;
